/**
 * PDF helper utilities for base64 handling, blob URL creation, and browser actions
 */

/**
 * Convert base64 string to Blob for PDF
 */
export const base64PdfToBlob = (base64: string): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: 'application/pdf' });
};

/**
 * Validate that decoded base64 data is actually a PDF
 * by checking for PDF magic bytes (%PDF-)
 */
export const isPdfContent = (byteArray: Uint8Array): boolean => {
  if (byteArray.length < 5) return false;

  // Check for PDF magic bytes: %PDF- (0x25 0x50 0x44 0x46 0x2D)
  const pdfMagic = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-

  for (let i = 0; i < pdfMagic.length; i++) {
    if (byteArray[i] !== pdfMagic[i]) {
      return false;
    }
  }

  return true;
};

/**
 * Safely convert base64 to PDF blob with validation
 */
export const safeBase64PdfToBlob = (base64: string): Blob | null => {
  try {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Validate PDF magic bytes
    if (!isPdfContent(byteArray)) {
      console.error('Invalid PDF content: missing PDF magic bytes');
      return null;
    }

    return new Blob([byteArray], { type: 'application/pdf' });
  } catch (error) {
    console.error('Failed to decode base64 PDF:', error);
    return null;
  }
};

/**
 * Create a blob URL from a Blob
 */
export const createBlobUrl = (blob: Blob): string => {
  return URL.createObjectURL(blob);
};

/**
 * Revoke a blob URL to free memory
 */
export const revokeBlobUrl = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Open PDF in a new browser tab
 */
export const openPdfInNewTab = (blobUrl: string): void => {
  window.open(blobUrl, '_blank');
};

/**
 * Trigger download of PDF file
 */
export const triggerDownload = (blobUrl: string, filename: string): void => {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Track active print iframes to prevent accumulation
const activePrintIframes = new Set<HTMLIFrameElement>();

/**
 * Clean up old print iframes
 */
const cleanupOldPrintIframes = (): void => {
  activePrintIframes.forEach(iframe => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
  });
  activePrintIframes.clear();
};

/**
 * Print PDF using hidden iframe technique
 */
export const printPdfInHiddenIframe = (blobUrl: string): void => {
  // Clean up any existing print iframes before creating a new one
  cleanupOldPrintIframes();
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  // Track this iframe
  activePrintIframes.add(iframe);

  // Helper to safely remove iframe
  const removeIframe = () => {
    if (document.body.contains(iframe)) {
      document.body.removeChild(iframe);
    }
    activePrintIframes.delete(iframe);
  };

  // Add timeout for iframe load to prevent memory leaks
  const loadTimeout = setTimeout(() => {
    console.error('PDF print iframe failed to load within 10 seconds');
    removeIframe();
  }, 10000);

  iframe.onload = () => {
    clearTimeout(loadTimeout);

    // Wait a bit for PDF to render in iframe
    setTimeout(() => {
      try {
        const contentWindow = iframe.contentWindow;
        if (!contentWindow) {
          console.error('Could not access iframe content window');
          document.body.removeChild(iframe);
          return;
        }

        // Listen for afterprint event to know when print dialog is closed
        contentWindow.addEventListener('afterprint', () => {
          // Remove iframe after print dialog is closed
          setTimeout(() => {
            removeIframe();
          }, 100);
        });

        // Also set a fallback timeout in case afterprint doesn't fire
        // (e.g., user navigates away or browser doesn't support afterprint)
        // Reduced from 60s to 10s to prevent accumulation
        setTimeout(() => {
          removeIframe();
        }, 10000); // Remove after 10 seconds as fallback

        // Trigger the print dialog
        contentWindow.print();
      } catch (error) {
        console.error('Error printing PDF:', error);
        removeIframe();
      }
    }, 250);
  };

  iframe.onerror = () => {
    clearTimeout(loadTimeout);
    console.error('Failed to load PDF in iframe');
    removeIframe();
  };
};

/**
 * Validate base64 string format
 */
export const isValidBase64 = (str: string): boolean => {
  if (!str || typeof str !== 'string') return false;
  try {
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
    if (!base64Regex.test(str)) return false;
    atob(str);
    return true;
  } catch {
    return false;
  }
};

/**
 * A4 aspect ratio constant (1:√2, approximately 1:1.414)
 */
export const A4_ASPECT_RATIO = 1.414;
